// ============================================
// PERSONALIZARMENU.JS — Helpers para el sidebar reordenable
// Aplica el orden personal del usuario sobre secciones + subs del menú.
// ============================================

import React from 'react';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ============================================
// Aplicación del orden personal
// ============================================
// ordenGuardado = { nivel1: [{item_id, orden}, ...], nivel2: { 'parent': [{item_id, orden}] } }
// Ítems no incluidos en nivel1 van al final, en orden default.
// Igual para subs.

export function aplicarOrden(secciones, ordenGuardado) {
  if (!ordenGuardado) return secciones;
  const mapaSecciones = new Map(secciones.map(s => [s.id, s]));
  const nivel1 = Array.isArray(ordenGuardado.nivel1) ? ordenGuardado.nivel1 : [];
  const idsEnOrden = nivel1
    .map(x => x.item_id)
    .filter(id => mapaSecciones.has(id));
  const idsGuardados = new Set(idsEnOrden);
  const resto = secciones.filter(s => !idsGuardados.has(s.id)).map(s => s.id);
  const idsFinal = [...idsEnOrden, ...resto];

  const nivel2 = ordenGuardado.nivel2 || {};
  return idsFinal.map(id => {
    const sec = mapaSecciones.get(id);
    if (!sec || !sec.subs) return sec;
    const ordenSubs = nivel2[sec.id] || [];
    const mapaSubs = new Map(sec.subs.map(s => [s.id, s]));
    const subsEnOrden = ordenSubs
      .map(x => x.item_id)
      .filter(sid => mapaSubs.has(sid));
    const subsGuardados = new Set(subsEnOrden);
    const restoSubs = sec.subs.filter(s => !subsGuardados.has(s.id)).map(s => s.id);
    const subsIds = [...subsEnOrden, ...restoSubs];
    return { ...sec, subs: subsIds.map(sid => mapaSubs.get(sid)).filter(Boolean) };
  }).filter(Boolean);
}

// ============================================
// Hook estándar de sensores (mouse + touch)
// ============================================

export function useSensoresDnD() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor,   { activationConstraint: { delay: 200, tolerance: 6 } }),
  );
}

// ============================================
// Ítem arrastrable — wrapper reutilizable
// ============================================

export function ItemArrastrable({ id, children, handleColor = 'rgba(255,255,255,0.4)' }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    display: 'flex', alignItems: 'stretch',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div
        {...listeners}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 32, minHeight: 44, cursor: 'grab',
          color: handleColor, fontSize: 18, flexShrink: 0,
          touchAction: 'none', userSelect: 'none',
        }}
        aria-label="Arrastrar"
      >
        ≡
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {children}
      </div>
    </div>
  );
}

// Re-export para que Dashboard.js tenga todo en un import
export { DndContext, closestCenter, SortableContext, arrayMove, verticalListSortingStrategy };

// ============================================
// Barra de personalización (banner sticky con los 3 botones)
// ============================================

export function BarraPersonalizar({ onGuardar, onCancelar, onRestablecer, esCelular, guardando }) {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 50,
      background: '#1a3a5c', color: '#fff',
      padding: '10px 12px', fontFamily: "'Poppins', sans-serif",
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    }}>
      <div style={{ fontSize: 11, opacity: 0.85, marginBottom: 6, lineHeight: 1.4 }}>
        {esCelular
          ? 'Mantén presionada una pestaña y arrástrala para moverla.'
          : 'Arrastra las pestañas para reordenar.'}
      </div>
      <div style={{
        fontSize: 10, opacity: 0.7, marginBottom: 10, lineHeight: 1.4,
        padding: '6px 8px', background: 'rgba(255,255,255,0.08)', borderRadius: 6,
      }}>
        💡 Tip: Haz click en una sección para abrirla y reordenar sus sub-menús.
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button
          onClick={onGuardar}
          disabled={guardando}
          style={{
            flex: 1, padding: '8px 10px', border: 'none', borderRadius: 8,
            background: '#5cd4b0', color: '#0b3b2d', fontSize: 12, fontWeight: 700,
            cursor: guardando ? 'not-allowed' : 'pointer', opacity: guardando ? 0.7 : 1,
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          {guardando ? 'Guardando…' : '✅ Guardar'}
        </button>
        <button
          onClick={onCancelar}
          disabled={guardando}
          style={{
            padding: '8px 10px', border: 'none', borderRadius: 8,
            background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: "'Poppins', sans-serif",
          }}
        >
          ❌ Cancelar
        </button>
        <button
          onClick={onRestablecer}
          disabled={guardando}
          style={{
            padding: '8px 10px', border: 'none', borderRadius: 8,
            background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: "'Poppins', sans-serif",
          }}
        >
          🔄 Restablecer
        </button>
      </div>
    </div>
  );
}
